import { useEffect, useRef } from 'react';
/**
 * Use the IntersectionObserver to track visibility of an element in the viewport.
 * @param {IntersectionObserverCallback} callback
 * @param {IntersectionObserverInit} options
 * @returns {UseIntersectionObserverReturn}
 */
function useIntersectionObserver(callback, options = {}) {
    const observerRef = useRef(null);
    const rootRef = useRef(null);
    const elementRef = useRef(null);
    // Hold the latest callback in a ref so the observer is created once on mount
    // rather than being disconnected and rebuilt on every render (callers commonly
    // pass fresh inline callbacks/options, which previously churned the observer).
    const callbackRef = useRef(callback);
    callbackRef.current = callback;
    useEffect(() => {
        if (!elementRef.current) {
            return undefined;
        }
        observerRef.current = new IntersectionObserver((entries, observer) => callbackRef.current(entries, observer), {
            root: rootRef.current,
            ...options,
        });
        observerRef.current.observe(elementRef.current);
        return () => {
            if (observerRef.current === null) {
                return;
            }
            observerRef.current.disconnect();
        };
        // Observer is intentionally created once; latest callback is read via ref.
    }, []);
    return { elementRef, observerRef, rootRef };
}
export default useIntersectionObserver;
